'use client';

import { useEffect, useMemo, useState } from 'react';
import { Box, HStack } from '@terra/ui-styled-system';
import {
  Badge,
  BodyDefault,
  Button,
  MetaDefault,
  Select,
  SettingsBooleanControl,
  SettingsCard,
  SettingsRow,
  SettingsSection,
  Spinner,
  createSelectListCollection,
} from '@terra/ui-design-system';
import {
  Copy01Icon,
  Plug01Icon,
  PowerOffIcon,
  ArrowDown01Icon,
} from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { useInternalAccessNetworks } from '../../hooks/api/useInternalAccessNetworks';
import type { InternalNetworkTunnelControls } from '../../hooks/useInternalNetworkTunnel';
import { useInternalNetworkTunnelVisibility } from '../../hooks/useInternalNetworkTunnelVisibility';
import { isInternalNetworkTunnelReplacingUpstream } from '../../electron-shared/utils/internal-network-tunnel-routing';

const resolveDefaultNetworkId = (
  networks: Array<{ value: string; isDefault: boolean }>,
): string | undefined =>
  networks.find((network) => network.isDefault)?.value ?? networks[0]?.value;

export function InternalNetworkTunnelSettingsTab({
  corporateUpstreamEnabled,
  status,
  isPending,
  start,
  stop,
  setRouteTorchThroughSession,
  setPreferredInternalAccessNetwork,
}: {
  corporateUpstreamEnabled: boolean;
} & InternalNetworkTunnelControls) {
  const t = useTranslations('settings.internalNetworkTunnel');
  const tSettings = useTranslations('settings');
  const tCommon = useTranslations('common');
  const showTunnel = useInternalNetworkTunnelVisibility();
  const {
    networks,
    isLoading: isNetworksLoading,
    isError: isNetworksError,
  } = useInternalAccessNetworks({
    enabled: showTunnel,
  });
  const [selectedNetworkId, setSelectedNetworkId] = useState<string>('');

  const sessionActive = status.connected || status.reconnecting === true;
  const showUpstreamConflict = isInternalNetworkTunnelReplacingUpstream({
    corporateUpstreamEnabled,
    status,
  });

  const networkItems = useMemo(
    () =>
      networks.map((network, index) => ({
        value: network.id,
        label:
          network.displayName ||
          network.slug ||
          t('networkFallback', { index: index + 1 }),
        isDefault: network.isDefault,
      })),
    [networks, t],
  );

  const networkCollection = useMemo(
    () =>
      createSelectListCollection({
        items: networkItems,
        itemToValue: (item) => item.value,
        itemToString: (item) => item.label,
      }),
    [networkItems],
  );

  useEffect(() => {
    const storedNetworkId =
      (status.connected ? status.internalAccessNetworkId : undefined) ??
      status.preferredInternalAccessNetworkId;

    if (networkItems.length === 0) {
      if (storedNetworkId) {
        setSelectedNetworkId(storedNetworkId);
      }

      return;
    }

    const preferredId =
      storedNetworkId ?? resolveDefaultNetworkId(networkItems);

    if (!preferredId) {
      return;
    }

    const isKnown = networkItems.some((item) => item.value === preferredId);
    const nextId = isKnown
      ? preferredId
      : resolveDefaultNetworkId(networkItems);

    if (nextId) {
      setSelectedNetworkId(nextId);
    }
  }, [
    networkItems,
    status.connected,
    status.internalAccessNetworkId,
    status.preferredInternalAccessNetworkId,
  ]);

  const copyValue = (value: string) => {
    void navigator.clipboard.writeText(value);
  };

  const httpProxyAddress = `127.0.0.1:${status.httpProxyPort ?? 0}`;
  const socksAddress = `127.0.0.1:${status.socksPort ?? 0}`;
  const statusBadge = status.connected
    ? {
        colorPalette: 'green' as const,
        label: t('connectedTo', {
          network: status.networkSlug ?? t('customerNetwork'),
        }),
      }
    : status.reconnecting
      ? { colorPalette: 'yellow' as const, label: tCommon('reconnecting') }
      : { colorPalette: 'gray' as const, label: t('notConnected') };

  const selectedNetworkLabel = networkItems.find(
    (item) => item.value === selectedNetworkId,
  )?.label;

  const handleNetworkChange = (networkId: string) => {
    setSelectedNetworkId(networkId);
    void setPreferredInternalAccessNetwork(networkId);
  };

  const handleConnect = () => {
    void start(selectedNetworkId || undefined);
  };

  const showNetworkPicker = networkItems.length > 1;

  return (
    <Box w="full" pt="4" borderTopWidth="1px" borderTopColor="border.subtle">
      <SettingsSection
        title={tSettings('proxyInternalNetworkTunnelTitle')}
        description={tSettings('proxyInternalNetworkTunnelDescription')}
        tooltip={t('description')}
        action={
          <HStack gap="2">
            <Button
              size="xs"
              variant="outline"
              onClick={handleConnect}
              disabled={isPending || sessionActive || isNetworksLoading}
              loading={isPending && !sessionActive}
            >
              <Plug01Icon size={14} />
              {tCommon('connect')}
            </Button>
            <Button
              size="xs"
              variant="outline"
              onClick={() => void stop()}
              disabled={isPending || !sessionActive}
              loading={isPending && sessionActive}
            >
              <PowerOffIcon size={14} />
              {tCommon('disconnect')}
            </Button>
          </HStack>
        }
      >
        <SettingsCard>
          {isNetworksLoading ? (
            <SettingsRow
              label={t('networkLabel')}
              value={<Spinner size="sm" />}
            />
          ) : isNetworksError ? (
            <SettingsRow
              label={t('networkLabel')}
              value={
                <MetaDefault color="fg.muted">
                  {t('networksListFailed')}
                </MetaDefault>
              }
            />
          ) : networkItems.length === 0 ? (
            <SettingsRow
              label={t('networkLabel')}
              value={
                <MetaDefault color="fg.muted">
                  {t('noNetworksAvailable')}
                </MetaDefault>
              }
            />
          ) : showNetworkPicker ? (
            <SettingsRow
              label={t('networkLabel')}
              tooltip={t('networkHelp')}
              control={
                <Box flexShrink={0} w="280px" maxW="min(280px, 55vw)">
                  <Select.Root
                    positioning={{
                      sameWidth: true,
                      strategy: 'fixed',
                      placement: 'bottom-start',
                    }}
                    size="sm"
                    width="full"
                    value={selectedNetworkId ? [selectedNetworkId] : []}
                    collection={networkCollection}
                    onValueChange={(details) => {
                      const networkId = details.value[0];

                      if (networkId) {
                        handleNetworkChange(networkId);
                      }
                    }}
                    disabled={sessionActive || isPending}
                  >
                    <Select.Control>
                      <Select.Trigger aria-label={t('networkLabel')}>
                        <Select.ValueText
                          placeholder={t('networkPlaceholder')}
                        />
                        <Select.Indicator>
                          <ArrowDown01Icon size={16} aria-hidden="true" />
                        </Select.Indicator>
                      </Select.Trigger>
                    </Select.Control>
                    <Select.Positioner>
                      <Select.Content maxH="240px" overflowY="auto">
                        {networkCollection.items.map((item) => (
                          <Select.Item key={item.value} item={item}>
                            <Select.ItemText>{item.label}</Select.ItemText>
                            <Select.ItemIndicator />
                          </Select.Item>
                        ))}
                      </Select.Content>
                    </Select.Positioner>
                  </Select.Root>
                </Box>
              }
            />
          ) : (
            <SettingsRow
              label={t('networkLabel')}
              value={
                <HStack gap="2">
                  <BodyDefault>{selectedNetworkLabel}</BodyDefault>
                  {networkItems[0]?.isDefault ? (
                    <Badge variant="outline" size="sm">
                      {tCommon('default')}
                    </Badge>
                  ) : null}
                </HStack>
              }
            />
          )}
          <SettingsRow
            label={tCommon('status')}
            value={
              <Badge
                variant="outline"
                size="sm"
                colorPalette={statusBadge.colorPalette}
              >
                {statusBadge.label}
              </Badge>
            }
          />
          {sessionActive ? (
            <SettingsRow
              label={t('httpConnectProxy')}
              value={httpProxyAddress}
              action={{
                label: tCommon('copy'),
                icon: <Copy01Icon size={16} />,
                onClick: () => copyValue(httpProxyAddress),
              }}
            />
          ) : null}
          {sessionActive ? (
            <SettingsRow
              label={t('socks5')}
              value={socksAddress}
              action={{
                label: tCommon('copy'),
                icon: <Copy01Icon size={16} />,
                onClick: () => copyValue(socksAddress),
              }}
            />
          ) : null}
          {sessionActive ? (
            <SettingsRow
              label={t('expires')}
              value={
                status.expiresAt
                  ? new Date(status.expiresAt).toLocaleString()
                  : t('expiresUnknown')
              }
            />
          ) : null}
          {sessionActive ? (
            <SettingsRow
              label={t('torchRouting')}
              tooltip={t('routeThroughSessionHelp')}
              control={
                <SettingsBooleanControl
                  checked={status.routeTorchThroughSession !== false}
                  label={
                    status.routeTorchThroughSession !== false
                      ? tCommon('enabled')
                      : tCommon('disabled')
                  }
                  disabled={isPending}
                  onCheckedChange={(details) =>
                    void setRouteTorchThroughSession(details.checked === true)
                  }
                />
              }
            />
          ) : null}
        </SettingsCard>
        {showUpstreamConflict ? (
          <MetaDefault color="fg.muted">
            {t('tunnelReplacesUpstream')}
          </MetaDefault>
        ) : null}
        {status.error ? (
          <MetaDefault color="fg.error">{status.error}</MetaDefault>
        ) : null}
      </SettingsSection>
    </Box>
  );
}
